"use client"

import type { TradeStatus } from "@/lib/util/trade"
import { aud, rrpOf } from "@/lib/util/trade"
import { sortVariants } from "@/lib/util/variants"
import LocalizedClientLink from "@/modules/common/components/localized-client-link"
import { HttpTypes } from "@medusajs/types"
import TradePrice from "../trade-price"
import ProductVariantsTable from "../product-variants-table"

type ProductActionsProps = {
  product: HttpTypes.StoreProduct
  region: HttpTypes.StoreRegion
  status?: TradeStatus
  tier?: string
  cartonExtra?: number
}

export default function ProductActions({ product, region, status = "guest", tier, cartonExtra = 0 }: ProductActionsProps) {
  const variants = sortVariants(product.variants ?? [])
  const optionLabel = (v: any) =>
    (v.options ?? [])
      .map((o: any) => o.value)
      .filter((x: string) => x && x !== "Default" && x !== "Default option value")
      .join(" / ") || v.title

  return (
    <div className="flex flex-col gap-5 w-full">
      <TradePrice product={product} status={status} tier={tier} size="page" />

      {status === "approved" ? (
        <ProductVariantsTable product={product} region={region} cartonExtra={cartonExtra} />
      ) : (
        <>
          {variants.length > 1 && (
            <ul className="divide-y divide-elevate-border rounded-image border border-elevate-border bg-white text-sm">
              {variants.map((v: any) => (
                <li key={v.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <span>{optionLabel(v)}</span>
                  <span className="text-elevate-grey-mid text-xs">{rrpOf(v) !== null ? `RRP ${aud(rrpOf(v)!)}` : "Trade only"}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="rounded-image bg-elevate-black text-white p-5 flex flex-col gap-3">
            <p className="font-heading text-2xl leading-tight">
              {status === "pending" ? "Application under review" : "Stock this product"}
            </p>
            <p className="text-sm text-neutral-300">
              {status === "pending"
                ? "Once approved you'll see your trade price and can order online. Our team is checking your details, usually within 1 business day."
                : "Trade pricing and online ordering are available to approved Australian retailers with an ABN."}
            </p>
            {status === "guest" && (
              <div className="flex flex-col xsmall:flex-row gap-2">
                <LocalizedClientLink href="/account?view=register" className="text-center bg-elevate-sage text-elevate-black px-5 py-3 text-xs uppercase tracking-[0.1em] hover:bg-white">
                  Apply for a trade account
                </LocalizedClientLink>
                <LocalizedClientLink href="/account" className="text-center border border-white/60 px-5 py-3 text-xs uppercase tracking-[0.1em] hover:bg-white hover:text-elevate-black">
                  Log in
                </LocalizedClientLink>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
